/**
 * @lines98/client/screens/daily-board-screen — daily-board hub (idle/done states).
 *
 * Per dispatch_t-7 §2 C1 + core-daily-board.md §2.3:
 *   - **Idle:** dateline (local-formatted), countdown к next rollover, «Играть доску
 *     дня» CTA, placeholder statistics card.
 *   - **Done:** «Сегодня уже сыграно» banner, score-verified display, «Пересмотреть
 *     свою партию» placeholder (replays — separate ticket), «К доске дня» self-route,
 *     countdown still ticking.
 *   - **Loading:** spinner while `GET /daily/seed` resolves.
 *   - **Error:** retry button когда server unreachable.
 *
 * Note: in-progress recovery (AC-5) handled inside this screen mount logic — if
 * daily-cache has `in_progress` record for the current dateOfDay, we replay
 * moves[] into а fresh engineState через store.startDailyGame() then re-apply
 * each move via store.attemptMove(). T-7 keeps refresh-recovery simple: only
 * после first valid move на board does cache get populated, so в worst case
 * player starts от scratch с same seed (deterministic, no progress loss in
 * the «boot before first move» window).
 *
 * **Мгновенный показ доски при возврате к незавершённой партии (owner-bug A 2026-06-28).**
 * Раньше любой возврат к начатой дневной партии (F5 / переход на др. экран и назад) рисовал
 * кадр «загрузка доски дня» (`renderLoading`), пока async-boot переигрывал ходы → доска
 * «собиралась» на глазах КАЖДЫЙ раз. Теперь два механизма убирают это:
 *   1. **Синхронный визуальный снимок доски** (`loadInProgressBoard`, зеркало
 *      `saveInProgress(…, boardView)`): mount-fast-path для in-progress рисует доску дня
 *      ВИЗУАЛЬНО МГНОВЕННО из снимка (`renderInProgressSnapshot`), БЕЗ текста «загрузка»;
 *      движок восстанавливается в ФОНЕ (boot → `loadInProgress` + replay под `setReplaying`,
 *      статично) и бесшовно сменяет кадр на board-screen (snapshot===восстановленное по
 *      детерминизму). Снимка нет (первый ход не снят / очищенный браузер) → нейтральный
 *      кадр-fallback.
 *   2. **Store-retained resume** (`resumeInProgress` same-game guard + `store.showBoard`):
 *      SPA-навигация НЕ стирает живую партию из стора (singleton) → при возврате движок
 *      переигрывать НЕ нужно, дешёвый phase-flip на board (без реконструкции). F5 (стор
 *      потерян) по-прежнему идёт через снимок + replay. Анти-чит цел: снимок/маркер — UX-
 *      проекция показа, КАНОН ходов в IDB, сервер авторитетен на finish (AC-13).
 *
 * UI gold-standard: docs/prototype/lines98-concept.html `view-daily` adapted к
 * full-screen (no sidebar/rail). Mode switcher hidden (core-daily-board.md §3.4).
 *
 * **Возврат на вкладку без вспышки стартовой (feat/fe-hydration п.2 / AC-2, owner-bug
 * 2026-06-19/20):** помимо синхронного in-progress-detect при mount (fast-path ниже, фикс
 * 2026-06-13), экран вешает `visibilitychange`/`pageshow`-подписку (`installVisibilityReturn`):
 * на возврат в видимое состояние / bfcache-restore СИНХРОННО перечитывает in-progress-маркер
 * (R-1) и, если партия начата, уводит в resume на board ТОЙ ЖЕ ветвью (`resumeInProgress`) —
 * idle-стартовая не мелькает ни на кадр. bfcache сохраняет DOM экрана как был (мог быть idle),
 * пока партия фактически идёт → без этой подписки владелец видел вспышку. Подписка снимается
 * в unmount. Маркер/снимок — ИСКЛЮЧИТЕЛЬНО UX-проекция показа; сервер авторитетен (AC-13).
 *
 * **Гейт на готовность идентичности в boot() (анти-чит, owner-bug 2026-06-27):** per-player
 * кэш-чтения (`loadPlayed` / `loadInProgress`) ключуются на `getCurrent()?.playerId`. На холодном
 * F5 экран монтируется ДО резолва `bootIdentity()` → `getCurrent()` null → `loadInProgress` даёт
 * false-negative → boot проскакивал в ИНТЕРАКТИВНЫЙ idle при наличии незавершённой партии (можно
 * было стартовать ту же фиксированную доску заново — обход «одной попытки»). boot() теперь
 * `await bootIdentity()` ПЕРЕД per-player проверками (прецедент profile-screen) → loadInProgress
 * видит верный ключ → resume вместо idle; до резолва держим нейтральный кадр. См. boot() ниже.
 *
 * Responsive: per .claude/skills/responsive-ui-design/SKILL.md — viewport-locked
 * (100dvh, no scroll), CSS Grid с auto/1fr/auto sections, container queries для
 * mini-preview sizing.
 *
 * Cross-references:
 *   - dispatch_t-7_game-developer.md §2 C1.
 *   - core-daily-board.md §2.3 + §5 (UX flow).
 *   - services/daily-api.ts (server seed).
 *   - services/daily-cache.ts (UX done-state preview + in-progress recovery).
 *   - services/countdown.ts (live HH:MM:SS).
 *   - store/game-store.ts (startDailyGame + showDaily phase).
 */

import { startState } from '@lines98/shared';

import { mountInfoPopover, type InfoPopoverHandle } from '../components/info-popover.js';
import { createStatPlate } from '../components/stat-plate.js';
import { createCountdownPlate } from '../components/countdown-plate.js';
import {
  ACTION_RAIL_FILL,
  attachActionRail,
  attachButtonGroupAutofit,
  type ActionRailHandle,
} from '../lib/action-rail.js';
import { copyShareLink } from '../lib/share-link.js';
import { attachAutofit, type AutofitHandle } from '../lib/autofit-text.js';
import {
  attachBoardNudge,
  scheduleSoftBeat,
  type BoardNudgeHandle,
  type SoftBeatHandle,
} from '../lib/attract-beat.js';
import { formatDateLabel } from '../lib/format.js';
import { SCREEN_TITLE_FILL } from '../lib/title-fit.js';
import { mountBoardView, type BoardViewHandle } from '../lib/board-view.js';
import { attachLiveAppearance, type LiveAppearanceHandle } from '../lib/board-appearance.js';
import {
  fetchFinalBoardByShortId,
  fetchOwnDailyStats,
  type OwnDailyStats,
} from '../lib/daily-final-state.js';
import { gateOwnDailyStats } from '../lib/daily-server-gate.js';
import {
  clearDailySeedCache,
  getCachedDailySeed,
  getDailySeed,
  type DailySeedResult,
} from '../services/daily-api.js';
import { submitBroughtFromYesterday } from '../lib/daily-finish.js';
import {
  clearIdleMarker,
  loadIdleMarker,
  loadInProgressBoard,
  loadInProgressMarker,
  loadPlayedSnapshot,
  saveIdleMarker,
  type InProgressBoardView,
} from '../lib/daily-played-snapshot.js';
import {
  clearInProgress,
  loadBroughtFromYesterday,
  loadInProgress,
  loadPlayed,
  savePlayed,
  type FinalBoardSnapshot,
  type PlayedRecord,
} from '../services/daily-cache.js';
import { bootIdentity } from '../services/identity.js';
import { t } from '../lib/i18n/index.js';
import { startCountdown, type CountdownHandle } from '../services/countdown.js';
import { getGrid, useGameStore } from '../store/game-store.js';
import { showToast } from '../ui/toast.js';
import type { ScreenHandle } from './board-screen.js';

/**
 * Descriptive copy для daily surface — single source per ui-components.md §1.3 P6
 * (surface owns text; InfoPopover only shows). Same string rendered (a) inline в
 * `.daily-sub` for desktop (CSS media query reveals); (b) в InfoPopover body на
 * mobile. Резолвится ФУНКЦИЕЙ (не const) при каждом рендере → активный язык (фаза
 * `daily` ре-монтируется на смену locale, app.ts gamePhaseLangChanged).
 */
const dailyDescriptiveCopy = (): string => t('game.dailyScreen.desc');

// =============================================================================
// Internal state (per-mount)
// =============================================================================

interface MountState {
  root: HTMLElement;
  countdown: CountdownHandle | null;
  /** Cached daily seed result — refresh button re-fetches. */
  seedResult: DailySeedResult | null;
  /** Played-today record, если present. */
  played: PlayedRecord | null;
  /** InfoPopover для descriptive copy — `?` icon на mobile, hidden на desktop. */
  infoPopover: InfoPopoverHandle | null;
  /** Общий action-rail kit (idle): autofit кнопок + компакт ⓘ/☰ на узкой панели. */
  actionRail: ActionRailHandle | null;
  /** Autofit текстовых элементов панели (eyebrow / title / sub / countdown / stats). */
  autofit: AutofitHandle | null;
  /** Реактивная привязка оформления (палитра×материал) к доске-превью (AC-2). */
  appearance: LiveAppearanceHandle | null;
  /**
   * Attract-беат CTA «Играть» (триггерный, owner-final 2026-06-28) — per-render idle:
   *   softBeat — отложенный мягкий намёк при заходе; nudge — сильный зов на промах-тап
   *   по доске-превью (lib/attract-beat, общий с board). Оба снимаются в teardownDynamic.
   */
  softBeat: SoftBeatHandle | null;
  nudge: BoardNudgeHandle | null;
  /**
   * Снятие visibility/pageshow-подписки этого mount'а (возврат на вкладку, feat/fe-hydration
   * п.2 / AC-2, owner-bug 2026-06-19/20). null до установки в mount; вызывается из unmount.
   * Отдельный per-mount teardown (НЕ в teardownDynamic — та снимает per-render handle'ы, а эта
   * подписка живёт весь lifetime экрана, как и `infoPopover`).
   */
  visibilityCleanup: (() => void) | null;
}

/**
 * Сбросить per-render handle'ы (autofit / action-rail / countdown) перед новым
 * рендером или unmount'ом. Каждый renderer создаёт DOM заново → старые handle'ы
 * наблюдают отсоединённые узлы; снимаем их.
 */
function teardownDynamic(state: MountState): void {
  // Постамент-grid (.postament-screen) — ТОЛЬКО для idle (board-day постамент).
  // loading/error/done — центрированные chrome-карты (.daily-screen базовый
  // flex-center). Снимаем класс перед любым ре-рендером; renderIdle добавит заново.
  state.root.classList.remove('postament-screen');
  if (state.countdown) {
    state.countdown.stop();
    state.countdown = null;
  }
  if (state.actionRail) {
    state.actionRail.detach();
    state.actionRail = null;
  }
  if (state.autofit) {
    state.autofit.detach();
    state.autofit = null;
  }
  if (state.appearance) {
    state.appearance.detach();
    state.appearance = null;
  }
  if (state.softBeat) {
    state.softBeat.cancel(); // отменить отложенный намёк, если idle сменился раньше срока
    state.softBeat = null;
  }
  if (state.nudge) {
    state.nudge.detach(); // снять слушатель промах-тапа по доске-превью
    state.nudge = null;
  }
}

// =============================================================================
// Public mount
// =============================================================================

export function mountDailyBoardScreen(host: HTMLElement): ScreenHandle {
  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css). Стоит на
  // ВСЕХ состояниях (idle/done постамент + loading/error chrome-карта); teardownDynamic
  // снимает только .postament-screen, .screen-root остаётся (feat/safe-area-trunk).
  root.className = 'daily-screen screen-root';
  host.appendChild(root);

  const state: MountState = {
    root,
    countdown: null,
    seedResult: null,
    played: null,
    infoPopover: null,
    actionRail: null,
    autofit: null,
    appearance: null,
    softBeat: null,
    nudge: null,
    visibilityCleanup: null,
  };

  // Seed-cache fast path (T-12, anti-cheat.md §3A.1 note): if a valid cached seed
  // exists (same session, rollover not crossed), paint the FINAL state synchronously
  // on mount — no loading flash, AND no idle→done / loading→idle flicker.
  //
  // **Синхронный статус-detect (instant-ui-hydration §1).** Статус дня читается
  // СИНХРОННО из localStorage-снимков В ТОТ ЖЕ ТИК. Все снимки date-stamped → на
  // rollover сами инвалидируются (никогда не показывают вчерашний кадр):
  //   1. played-снимок (`loadPlayedSnapshot`, зеркало `savePlayed`) — «сыграно сегодня»
  //      → `renderDone` СРАЗУ (финальная доска из снимка), без idle→done flip (#8);
  //   2. **in-progress-маркер (`loadInProgressMarker`, зеркало `saveInProgress`)** —
  //      «партия НАЧАТА, но не доиграна» → НЕ рисуем стартовую страницу доски дня вообще:
  //      нейтральный кадр + boot уведёт в resume на board (owner-bug 2026-06-13 «мелькание
  //      стартовой при возврате к начатой партии»). Раньше тут падали на idle-маркер (он
  //      оставался с захода ДО старта) → ПОЛНАЯ стартовая страница (доска+описание+кнопки)
  //      → резкий флип в resume, когда async-`loadInProgress` находил партию. Теперь
  //      синхронный mount знает «партия начата» в тот же тик и стартовую НЕ рисует;
  //   3. idle-маркер (`loadIdleMarker`, ставит boot при подтверждённом «не сыграно»)
  //      — «сегодня подтверждённо НЕ сыграно» → `renderIdle` СРАЗУ (стартовая доска
  //      из cached seed), БЕЗ промежуточного нейтрального loading-кадра (2026-06-11
  //      «idle-доска мигает при каждом заходе»);
  //   4. все пусты → статус СИНХРОННО НЕИЗВЕСТЕН (первый в этой сессии заход /
  //      pre-#8 / кросс-девайс / очищенный браузер) → НЕЙТРАЛЬНЫЙ кадр (`renderLoading`),
  //      НЕ приглашение и НЕ done. По §1/§5 нельзя рисовать неверный idle-кадр для дня,
  //      который МОГ быть уже сыгран — это и была остаточная вспышка IDLE→DONE.
  // Приоритет played > in-progress > idle (done — терминальное состояние, вытесняет всё;
  // маркеры взаимоисключающие по write-path'у, но порядок defensive). Boot затем async-
  // резолвит истину (IDB `loadPlayed` / `loadInProgress`) → DONE / RESUME / IDLE
  // идемпотентным reconcile'ом. На cache-miss (холодный seed) — тот же нейтральный кадр.
  const cached = getCachedDailySeed();
  if (cached) {
    state.seedResult = cached;
    const playedSnapshot = loadPlayedSnapshot(cached.dateOfDay);
    if (playedSnapshot) {
      // Known-played синхронно → done без промежуточного кадра. Boot — идемпотентный
      // reconcile (server-truth): подтверждает/поправляет played + ранг, без перерисовки.
      state.played = playedSnapshot;
      renderDone(state);
      void boot(state, { skipFetch: true, alreadyRendered: true });
    } else if (loadInProgressMarker(cached.dateOfDay)) {
      // Known-in-progress синхронно → НЕ рисуем стартовую страницу (idle/done). Если есть
      // ВИЗУАЛЬНЫЙ снимок доски — рисуем доску дня МГНОВЕННО из снимка (БЕЗ кадра «загрузка»
      // и без видимой переигровки — owner-bug A 2026-06-28). Снимка нет (первый ход ещё не
      // снят / очищенный браузер) → нейтральный кадр-fallback. В ОБОИХ случаях boot async-
      // читает IDB-канон `loadInProgress` и уводит в resume на board: при store-retained
      // партии — дешёвым phase-flip без переигровки, при F5 — реплеем под `setReplaying`
      // (статично, поверх уже показанного снимка → бесшовно). `resumeIntent` помечает путь.
      const ipBoard = loadInProgressBoard(cached.dateOfDay);
      if (ipBoard) {
        renderInProgressSnapshot(state, ipBoard);
      } else {
        renderLoading(state);
      }
      void boot(state, { skipFetch: true, resumeIntent: true });
    } else if (loadIdleMarker(cached.dateOfDay)) {
      // Known-not-played синхронно → idle СРАЗУ (стартовая доска-превью из cached seed),
      // без нейтрального кадра. Boot — идемпотентный reconcile: подтверждает «не сыграно»
      // (no-op, эталон-превью НЕ пере-рендерится — guard `idleAlreadyRendered`) либо
      // поправляет в done/recovery, если статус сменился (кросс-девайс с прошлого захода).
      renderIdle(state);
      void boot(state, { skipFetch: true, idleAlreadyRendered: true });
    } else {
      // Статус синхронно НЕИЗВЕСТЕН → нейтральный кадр; boot async-резолвит в idle
      // (+ставит idle-маркер) или done (сыграно — pre-#8 / кросс-девайс, + бэкфилл).
      renderLoading(state);
      void boot(state, { skipFetch: true });
    }
  } else {
    renderLoading(state);
    void boot(state);
  }

  // ВОЗВРАТ НА ВКЛАДКУ (feat/fe-hydration п.2 / AC-2, owner-bug 2026-06-19/20). Фикс
  // 2026-06-13 закрыл ТОЛЬКО свежий mount (синхронный in-progress-detect выше). Но
  // владелец ВСЁ РАВНО видел вспышку стартовой страницы при ВОЗВРАТЕ на вкладку
  // (`visibilitychange`/`pageshow`/bfcache-restore) к уже НАЧАТОЙ партии: на этом пути у
  // daily-screen НЕ было обработчика, а bfcache-restore сохраняет DOM экрана как был (idle-
  // приглашение), пока партия фактически идёт. Закрываем КОРЕНЬ именно тут (а не общим
  // shell-хэндлером) — это daily-специфика и единственный экран, у которого «стартовая
  // страница» конкурирует с in-progress. Подписка живёт весь lifetime mount'а (снимается в
  // unmount). REUSE R-1: тот же синхронный детект (`getCachedDailySeed` + `loadInProgressMarker`)
  // и ТА ЖЕ resume-ветка (`resumeInProgress`), что и mount — без второго кэша/маркера.
  state.visibilityCleanup = installVisibilityReturn(state);

  return {
    unmount(): void {
      if (state.visibilityCleanup) {
        state.visibilityCleanup();
        state.visibilityCleanup = null;
      }
      teardownDynamic(state);
      if (state.infoPopover) {
        state.infoPopover.unmount();
        state.infoPopover = null;
      }
      host.innerHTML = '';
    },
  };
}

/**
 * Подписка «возврат на вкладку» (feat/fe-hydration п.2 / AC-2). На каждый возврат в
 * видимое состояние (`visibilitychange`→visible) ИЛИ восстановление страницы (`pageshow`,
 * в т.ч. bfcache `persisted`) СИНХРОННО перечитываем in-progress-маркер (R-1) и, если
 * сегодня есть НЕЗАВЕРШЁННАЯ начатая партия, уводим в resume на `board` ТОЙ ЖЕ ветвью
 * (`resumeInProgress`), что и mount — игрок никогда не видит idle-стартовую вспышку при
 * наличии начатой партии (AC-1/AC-2). Когда партии НЕТ — НИЧЕГО не делаем (idle корректен,
 * P1 не приносится в жертву, AC-4). НЕ гейтит видимое на сеть (синхронный read, AC-6).
 *
 * Анти-чит (AC-13): маркер — UX-проекция показа (lib/daily-played-snapshot), не вход в
 * движок/верификацию; seed+moves восстанавливаются локальным детерминированным реплеем
 * (`resumeInProgress` → server-truth остаётся авторитетным на finish).
 *
 * @returns teardown — снимает оба слушателя (симметрия shell-подписок).
 */
function installVisibilityReturn(state: MountState): () => void {
  const onReturn = (): void => {
    // Экран мог смениться, пока вкладка была скрыта (guard isConnected) — не трогаем.
    if (!state.root.isConnected) return;
    // Синхронный статус-detect (R-1) — ровно как mount-fast-path. Нет seed (cache-miss) →
    // нечего решать синхронно (boot/heartbeat дотянут истину); не рисуем неверный кадр.
    const cached = getCachedDailySeed();
    if (!cached) return;
    // Партия НАЧАТА сегодня (синхронный маркер) → resume на board ТОЙ ЖЕ ветвью, что mount.
    // Канон seed+moves — IDB `loadInProgress`; маркер лишь снимает гейт «рисовать стартовую».
    if (!loadInProgressMarker(cached.dateOfDay)) return; // партии нет → idle корректен (AC-4)
    void resumeFromMarker(state, cached.dateOfDay);
  };
  // visibilitychange шлётся И на скрытие — реагируем ТОЛЬКО на возврат в видимое (как
  // session-heartbeat / leaderboard focus-хендлеры). pageshow покрывает bfcache-restore.
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') onReturn();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', onReturn);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', onReturn);
  };
}

/**
 * Возврат к начатой партии: читаем IDB-канон `loadInProgress` (seed+moves) и уводим в
 * resume на board ТОЙ ЖЕ детерминированной ветвью, что mount (`resumeInProgress`). Если
 * записи внезапно нет (кэш очищен между записью маркера и возвратом) — no-op: оставляем
 * текущий кадр, boot/heartbeat дотянут истину (НЕ рисуем неверный idle). played-проверку
 * тут НЕ делаем: если день уже сыгран, маркер снят тем же write-path'ом (`savePlayed`).
 */
async function resumeFromMarker(state: MountState, dateOfDay: string): Promise<void> {
  const inProgress = await loadInProgress(dateOfDay);
  if (!inProgress) return; // запись пропала → не трогаем экран (boot-reconcile разрулит)
  if (!state.root.isConnected) return; // экран сменился, пока летел IDB-read
  resumeInProgress(dateOfDay, inProgress.seed, inProgress.moves);
}

/**
 * Mount InfoPopover on `.daily-title-row` for descriptive copy. Tears down any
 * prior popover before mounting a new one (re-renders reuse the same surface).
 */
function attachInfoPopover(state: MountState, title: string): void {
  if (state.infoPopover) {
    state.infoPopover.unmount();
    state.infoPopover = null;
  }
  // Host = .daily-titleline (строка «Доска дня · дата») → «?» встаёт СПРАВА от
  // заголовка (owner-directive 2026-05-29). Поповер: title «Сегодняшняя раздача»
  // + описательный текст (то, что убрано из строки над доской).
  const triggerHost = state.root.querySelector<HTMLElement>('.daily-titleline');
  if (!triggerHost) return;
  state.infoPopover = mountInfoPopover({
    title,
    bodyText: dailyDescriptiveCopy(),
    triggerHost,
    variant: '?',
  });
}

// =============================================================================
// Boot flow — fetch seed → decide state
// =============================================================================

async function boot(
  state: MountState,
  options: {
    skipFetch?: boolean;
    alreadyRendered?: boolean;
    idleAlreadyRendered?: boolean;
    resumeIntent?: boolean;
  } = {},
): Promise<void> {
  // skipFetch: the seed-cache fast path already set `state.seedResult` and painted
  // either DONE (played-snapshot), IDLE (idle-marker) or a NEUTRAL frame (status
  // unknown). Reuse the seed — no network — and run the local played / in-progress
  // checks below; the neutral case then resolves to done or idle. On a fresh mount
  // (cache miss) we fetch as before.
  //
  // alreadyRendered: mount synchronously painted `renderDone` from the played-snapshot
  // (instant-ui-hydration §1). This boot is then a pure IDEMPOTENT RECONCILE — it
  // re-reads the IDB-canon `played` and re-renders ONLY if server/canon truth actually
  // disagrees with what's on screen (rare: snapshot stale or never-played). On
  // agreement it's a no-op → the done board/plates/buttons are NOT repainted (no flip).
  //
  // idleAlreadyRendered: mount synchronously painted `renderIdle` from the idle-marker
  // (instant-ui-hydration §1, 2026-06-11). Symmetric idempotent reconcile — on agreement
  // («не сыграно» confirmed, no in-progress) it's a NO-OP: the etalon board-preview is
  // NOT re-mounted (no flicker). It re-renders only if the status actually changed since
  // the marker was written (player played on another device → done; or an in-progress
  // attempt exists → recovery deep-link).
  //
  // resumeIntent: mount read the synchronous in-progress-marker (started-but-unfinished)
  // and painted ONLY a NEUTRAL frame — NOT the daily start page (owner-bug 2026-06-13).
  // Boot reads the IDB-canon `loadInProgress` and deep-links into resume on `board`,
  // so the player never sees the idle hub flash. Defensive fall-throughs: if the IDB
  // record is unexpectedly gone (cache cleared between marker-write and this read) or
  // the day was actually played/rolled-over, we resolve to done/idle as usual.
  if (!options.skipFetch) {
    const seedResult = await getDailySeed();
    state.seedResult = seedResult;
  }
  const seedResult = state.seedResult;

  if (!seedResult || !seedResult.ok) {
    renderError(state, seedResult ?? { ok: false, error: 'network' });
    return;
  }

  // ГЕЙТ НА ГОТОВНОСТЬ ИДЕНТИЧНОСТИ ПЕРЕД per-player проверками (анти-чит, owner-bug
  // 2026-06-27 «F5 на доске дня → интерактивный idle → можно стартовать ту же доску
  // заново»). КОРЕНЬ: все per-player кэш-чтения ниже (`loadPlayed` / `loadInProgress` /
  // `loadBroughtFromYesterday`) ключуются на `getCurrent()?.playerId` СИНХРОННО в момент
  // вызова. На холодном F5 экран монтируется из mountApp ДО резолва `bootIdentity()` (IDB-
  // загрузка/mint анонима — async), поэтому `getCurrent()` ещё null → `loadInProgress`
  // отдаёт FALSE-NEGATIVE `null` (нет ключа) → boot проскакивал в ИНТЕРАКТИВНЫЙ `renderIdle`,
  // хотя незавершённая партия в IDB есть. В этом окне можно было нажать «Играть доску дня» и
  // начать ту же (фиксированный seed) доску заново — обход «одной попытки» (сервер блокирует
  // лишь ПОВТОРНЫЙ submit, не множественные попытки до первого). Позже идентичность догружалась
  // и visibilitychange-резюм (`installVisibilityReturn`) уводил в начатую партию — отсюда и
  // «idle → подождать → переключить вкладку → доска вернулась». ФИКС (прецедент
  // profile-screen.ts `load()`): ДОЖДАТЬСЯ `bootIdentity()` (идемпотентен — общий in-flight
  // промис, на повторных заходах резолвится мгновенно) → per-player чтения ниже видят верный
  // ключ → `loadInProgress` находит партию → resume на board, без интерактивного idle-окна. До
  // резолва экран держит НЕЙТРАЛЬНЫЙ кадр (`renderLoading`, без кнопки старта — fast-path выше
  // на F5 даёт case 4, т.к. синхронные маркеры тоже keyed по identity). Гость/offline (mint не
  // удался) → `getCurrent()` остаётся null → idle (fail-open): у гостя per-player in-progress
  // не персистится (saveInProgress no-op без ключа), переигрывать нечего, сервер backstop на
  // сабмите. Анти-чит цел: маркеры/кэш — UX-проекция, движок/верификацию не трогаем (AC-13).
  await bootIdentity();
  if (!state.root.isConnected) return; // экран сменился, пока резолвилась идентичность

  // Trigger B (T-12, core-daily-board.md §7.3 + AC-12): the player left an
  // in-progress attempt and came back after rollover. The cached record is for a
  // PAST day → re-simulate + submit it as `finish_reason='rollover'` into THAT
  // day's leaderboard, toast the final score, clear the stale record, then fall
  // through to the new day's idle/played/in-progress checks. The attempt is not
  // lost — it counts. (Offline: on a non-consuming submit failure the record is
  // kept and re-tried on the next boot — §7.1.)
  await maybeAutoFinishBroughtFromYesterday(seedResult.dateOfDay);

  // Check played-today cache (IDB-canon — UX-only; server still authoritative).
  const playedCanon = await loadPlayed(seedResult.dateOfDay);

  if (options.alreadyRendered) {
    // Mount already painted done from the synchronous snapshot. RECONCILE only:
    //   - canon agrees (played) → NO-OP. We keep the rendered done untouched (no
    //     board re-mount). `hydrateDone` (kicked by the sync renderDone) already
    //     reconciles rank / cross-device board against server-truth idempotently.
    //   - canon disagrees (no played record — snapshot was stale / mirror drift) →
    //     correct downward to idle so we don't falsely lock the player out.
    if (!playedCanon) {
      state.played = null;
      renderIdle(state);
      // Not-played confirmed → seed the idle-marker so the NEXT mount is instant idle.
      saveIdleMarker(seedResult.dateOfDay);
    }
    // else: leave the done screen as-is (state.played already set at mount).
    return;
  }

  if (options.idleAlreadyRendered) {
    // Mount synchronously painted IDLE from the idle-marker. RECONCILE only:
    //   - canon agrees (not played) AND no in-progress → NO-OP. The etalon board-
    //     preview stays as-is (NOT re-mounted) → no flicker. Re-affirm the marker
    //     (idempotent — already today's) so it never silently lapses.
    //   - player actually played since the marker (cross-device) → render done.
    //   - an in-progress attempt exists → deep-link into recovery.
    if (playedCanon) {
      state.played = playedCanon;
      renderDone(state);
      return;
    }
    const inProgressIdle = await loadInProgress(seedResult.dateOfDay);
    if (inProgressIdle) {
      resumeInProgress(seedResult.dateOfDay, inProgressIdle.seed, inProgressIdle.moves);
      return;
    }
    // Confirmed idle (LOCALLY) — keep the already-painted screen untouched (no re-render).
    saveIdleMarker(seedResult.dateOfDay);
    // Server-truth reconcile: this device's local cache says «not played», but the
    // ACCOUNT may have played on another browser (the cross-browser double-play bug).
    // Fire-and-forget the server check; if the server says «played», flip idle→done.
    void reconcileServerTruthOnIdle(state, seedResult.dateOfDay);
    return;
  }

  if (options.resumeIntent) {
    // Mount read the in-progress-marker → painted ONLY a neutral frame. Resolve:
    //   - day actually played (cross-device finish since the marker) → done;
    //   - in-progress record present (the expected path) → resume on board (no idle flash);
    //   - record gone / day rolled over (marker stale beyond date check) → idle, as usual.
    if (playedCanon) {
      state.played = playedCanon;
      renderDone(state);
      return;
    }
    const inProgressResume = await loadInProgress(seedResult.dateOfDay);
    if (inProgressResume) {
      resumeInProgress(seedResult.dateOfDay, inProgressResume.seed, inProgressResume.moves);
      return;
    }
    // No record after all → behave like the cold idle resolution (paint idle, seed the
    // marker, server-truth reconcile). The stale in-progress-marker is harmless (it's
    // date-stamped; `loadInProgress` already cleared a rollover-stale IDB record).
    renderIdle(state);
    saveIdleMarker(seedResult.dateOfDay);
    void reconcileServerTruthOnIdle(state, seedResult.dateOfDay);
    return;
  }

  state.played = playedCanon;

  if (state.played) {
    renderDone(state);
    return;
  }

  // Check for in-progress cache (refresh-recovery, AC-5). If found, deep-link
  // back into board with the recovered state.
  const inProgress = await loadInProgress(seedResult.dateOfDay);
  if (inProgress) {
    // Recovery path: jump straight к board with recovered seed + replay moves.
    resumeInProgress(seedResult.dateOfDay, inProgress.seed, inProgress.moves);
    return;
  }

  // Idle — now KNOWN not-played (async confirmed). Mount painted a NEUTRAL frame
  // (renderLoading), NOT an idle invite, precisely so we never showed the wrong
  // «Играть доску дня» card for a day that turned out to be already played. This is
  // the first moment the idle invite is correct → paint it now (warm board-preview
  // via the cached seed). Holds for both paths (cache-miss fetch + cached-seed-but-
  // unknown-played), so we no longer gate it on `!skipFetch`.
  renderIdle(state);
  // Seed the date-stamped idle-marker: next mount reads it synchronously and paints
  // idle WITHOUT this neutral loading frame (2026-06-11 «idle мигает каждый заход»).
  // Date-stamped → self-invalidates on rollover; cleared when the player plays.
  saveIdleMarker(seedResult.dateOfDay);
  // Server-truth reconcile (cross-browser double-play fix): local cache said «not
  // played», but the ACCOUNT may have an attempt on another browser. Background-check
  // the server; if it returns the account's row, flip idle→done + backfill locally.
  void reconcileServerTruthOnIdle(state, seedResult.dateOfDay);
}

/**
 * Фоновой server-truth reconcile на idle-пути (cross-browser double-play fix,
 * owner-bug 2026-06-11). Локальный device-кэш (`loadPlayed`) уже сказал «на ЭТОМ
 * устройстве не сыграно» → нарисован idle. Но ЭТОТ АККАУНТ мог сыграть доску на
 * ДРУГОМ браузере — истина в серверном `daily_attempts`, выдаётся как `me`-блок
 * (`gateOwnDailyStats` → `fetchOwnDailyStats`). Если сервер вернул запись:
 *   - backfill локально (`savePlayed` → IDB-канон + синхронный played-снимок; следующий
 *     mount мгновенно done; done-доска пересимулируется по shortId в `hydrateDone`);
 *   - снимаем idle-маркер (`clearIdleMarker`), чтобы не залип idle поверх сыгранной доски
 *     (savePlayed уже снимает его тем же write-path'ом, но снимаем явно — defensive);
 *   - флип idle→done (`renderDone`).
 *
 * Идемпотентно (instant-ui-hydration §3) и БЕЗ дубль-запросов: `gateOwnDailyStats`
 * дедупит с await-на-клике (#3) и кэширует ответ в сессии. Guard `state.root.isConnected`
 * — экран мог смениться, пока летел запрос.
 *
 * **Fail-OPEN на сети:** `fetchOwnDailyStats` отдаёт `null` И на «не играл», И на сетевом
 * сбое (не различает) → остаёмся в idle. Это намеренно: локальный гейт уже корректен,
 * сервер backstop-ит на сабмите. Не запираем игрока из-за упавшей сети.
 */
async function reconcileServerTruthOnIdle(state: MountState, dateOfDay: string): Promise<void> {
  const stats = await gateOwnDailyStats(dateOfDay);
  if (!stats) return; // null = «не играл» / гость / сетевой сбой → остаёмся idle (fail-open)
  if (!state.root.isConnected) return; // экран сменился, пока летел запрос
  // Не перерисовываем, если экран уже ушёл в done/recovery другим путём (гонка mount-boot).
  if (state.played) return;
  backfillPlayedFromServerStats(state, dateOfDay, stats);
  renderDone(state);
}

/**
 * Записать played-факт из server-truth (`OwnDailyStats`) в локальный кэш и `state.played`,
 * чтобы done-доска пересимулировалась по `shortId` (в `hydrateDone`) и следующие mount'ы
 * были мгновенно done. `finalBoard` НЕ кладём (на чужом устройстве снимка нет — `hydrateDone`
 * восстановит доску из реплея по `shortId`). `savePlayed` зеркалит снимок + снимает idle-маркер
 * одним write-path'ом. Общий для idle-reconcile и hard-гейта на клике (не дублируем).
 */
function backfillPlayedFromServerStats(
  state: MountState,
  dateOfDay: string,
  stats: OwnDailyStats,
): void {
  const record: PlayedRecord = {
    v: 1,
    dateOfDay,
    scoreVerified: stats.scoreVerified,
    replayId: null,
    linesVerified: stats.linesVerified,
    shortId: stats.shortId,
    lastRank: stats.rank,
    timestamp: Date.now(),
  };
  state.played = record;
  clearIdleMarker(); // idle больше не корректен (defensive — savePlayed тоже снимет)
  void savePlayed(record); // IDB-канон + синхронный played-снимок (один write-path)
}

/**
 * Resume an in-progress attempt: start game с the cached seed, replay each cached
 * move через store.attemptMove(). The store's `applyMove` is deterministic, so
 * pre-replay state === post-replay state (per replayScore invariant). Per
 * AC-5 + core-daily-board.md §4.3.
 *
 * **Recovery is SILENT (board-feedback-animations.md §2.4 / AC-13).** `startDailyGame`
 * synchronously fires the phase-router → board-screen mounts and subscribes BEFORE
 * the replay loop runs. The loop then grows `moves` UNDER that live subscription, so
 * each replayed move would otherwise trigger the spawn-pop appearance animation AND
 * the line-clear celebration overlay — the player would see the pops/celebrations of
 * a partially-played game re-enacted on every refresh. We wrap the loop in the
 * store's `replaying` guard (`setReplaying(true)` … `setReplaying(false)`), which
 * makes `renderBoardDiff` paint the recovered state STATICALLY (no spawn-pop, no
 * overlays). Once the guard is cleared, normal play animates as usual.
 */
function resumeInProgress(
  dateOfDay: string,
  seed: number,
  moves: ReadonlyArray<{ sr: number; sc: number; r: number; c: number }>,
): void {
  const store = useGameStore.getState();

  // STORE-RETAINED resume (owner-bug A 2026-06-28). Стор — singleton в памяти: SPA-навигация
  // (доска дня → лидерборд → доска дня) НЕ стирает живую партию (`showDaily` не трогает
  // engineState/moves/context). Если стор УЖЕ держит ЭТУ же партию доски дня (тот же
  // dateOfDay+seed+число ходов, движок жив, не финиширована) — НИЧЕГО реконструировать не
  // надо: повторный `startDailyGame`+реплей пере-смонтировали бы board и заново проиграли
  // recovery (spawn-«бип»/оверлеи). Достаточно вернуть фазу на board (без reset/replay):
  //   · phase==='board' (возврат вкладки / pageshow при неизменной партии) → no-op (AC-10);
  //   · phase!=='board' (SPA-возврат на доску дня) → дешёвый phase-flip `showBoard()` —
  //     board-screen смонтируется на живом engineState, БЕЗ переигровки (нет «загрузки»).
  // Re-replay (ниже) — ТОЛЬКО когда стор НЕ на этой партии (F5: движок в памяти потерян).
  const sameGameInStore =
    store.context.kind === 'daily' &&
    store.context.dateOfDay === dateOfDay &&
    store.seed === seed &&
    store.moves.length === moves.length &&
    !store.finishReason &&
    getGrid(store) !== null;
  if (sameGameInStore) {
    // Уже ЖИВ на board этой партии (возврат вкладки / pageshow / bfcache при неизменной
    // партии) → no-op, «бип» не звучит (AC-10).
    if (store.phase === 'board') return;
    // SPA-удержанная партия, но фаза другая (возврат на доску дня) → дешёвый phase-flip на
    // board БЕЗ переигровки (движок жив в сторе).
    store.showBoard();
    return;
  }

  store.startDailyGame({ seed, dateOfDay });

  // Guard ON before the first replayed move grows `moves` under the board-screen
  // subscription. Set AFTER startDailyGame (which resets `replaying` to false).
  useGameStore.getState().setReplaying(true);
  try {
    // Replay cached moves. Each store.selectCell + attemptMove pair is idempotent
    // против applyMove — same seed + same moves produces same state.
    for (const m of moves) {
      useGameStore.getState().selectCell(m.sr, m.sc);
      const res = useGameStore.getState().attemptMove(m.r, m.c);
      if (!res.ok) {
        // Cache corruption / engine version drift. Skip this move; remaining moves
        // also won't replay correctly. Conservative: start fresh с same seed.
        // startDailyGame resets `replaying` to false → re-arm so the (empty) fresh
        // start also paints static; the `finally` clears it afterwards.
        useGameStore.getState().startDailyGame({ seed, dateOfDay });
        useGameStore.getState().setReplaying(true);
        break;
      }
    }
  } finally {
    // Recovery done — re-enable normal in-play animations. The static render of the
    // recovered state already happened under the guard; from here new moves animate.
    useGameStore.getState().setReplaying(false);
  }
  // store.phase is already 'board' (set by startDailyGame); app.ts will mount
  // the board screen on the next subscribe-fire. No explicit showBoard needed.
}

/**
 * Trigger B (T-12): if the in-progress cache holds an attempt from a PAST day
 * (rollover happened while the tab was closed — core-daily-board.md §7.3), submit
 * it as `finish_reason='rollover'` into that day's leaderboard, toast the score,
 * and clear the stale record. The heavy lifting (re-simulate + submit + cache
 * discipline) lives in the shared `lib/daily-finish` module.
 *
 * On a non-consuming submit failure (network / rate_limited / engine_mismatch) the
 * record is kept (NOT cleared) so the next boot re-tries it (§7.1 offline retry).
 * We still fall through to the new day's idle — the player isn't blocked.
 *
 * @param currentDateOfDay — today's `date_of_day`.
 */
async function maybeAutoFinishBroughtFromYesterday(currentDateOfDay: string): Promise<void> {
  const record = await loadBroughtFromYesterday(currentDateOfDay);
  if (!record) return;
  // Empty attempt (zero moves) — never «started» per §2.2; nothing to submit AND
  // nothing to credit. `loadBroughtFromYesterday` intentionally does NOT delete
  // (the heavy paths re-submit first), so we must DROP it ourselves here — else a
  // zero-move past-day ghost lingers in IDB and is re-read on every entry forever
  // (stale-attempt lifecycle leak, owner-playtest 2026-06-17).
  if (record.moves.length === 0) {
    await clearInProgress();
    return;
  }
  const { result, score } = await submitBroughtFromYesterday(record, currentDateOfDay);
  // Toast «closed by timer» ONLY when the past-day attempt was actually CREDITED
  // into its day's leaderboard (a verified rollover accept within the late-submit
  // window). On `duplicate_attempt` / `rejected` (incl. the `unknown` late-reject
  // for an attempt past the window, or a day already played) the attempt is
  // consumed/un-creditable and the stale record was just dropped — a «closed by
  // timer — счёт N» toast there is MISLEADING (the score wasn't published; the day
  // is gone). So we stay silent: the stale ghost is cleaned up invisibly and the
  // player proceeds to today's fresh board. On a non-consuming failure the record
  // is retained for retry → also no toast (§7.1 offline; not yet resolved).
  if (result.status === 'verified') {
    showToast(t('game.dailyScreen.toastRollover', { score: formatNumber(score) }));
  }
}

// =============================================================================
// Renderers — one per state
// =============================================================================

function renderLoading(state: MountState): void {
  teardownDynamic(state);
  state.root.innerHTML = `
    <div class="daily-card daily-card-loading">
      <div class="daily-loading">${escapeHtml(t('game.dailyScreen.loading'))}</div>
    </div>
  `;
}

/**
 * In-progress RESUME-кадр (owner-bug A 2026-06-28): рисует доску дня ВИЗУАЛЬНО МГНОВЕННО из
 * синхронного снимка при возврате к незавершённой партии — БЕЗ кадра «загрузка доски дня» и
 * без видимой переигровки ходов. Структурно ЗЕРКАЛИТ board-screen postament (заголовок +
 * доска + ряд из 2 кнопок, БЕЗ secondary-ряда — как board-screen) → когда boot восстановит
 * движок и app.ts смонтирует board-screen, ДОСКА остаётся на том же месте (бесшовно),
 * сменяется лишь обвязка. Это ТРАНЗИЕНТНЫЙ скелет загрузки: доска — ЕДИНЫЙ модуль board-view
 * (статично из снимка, как idle/done), кнопки — disabled-плейсхолдеры (движок ещё не жив;
 * board-screen навесит рабочие «Завершить»/«Новая игра»). Снимок === восстановленному
 * состоянию по детерминизму движка → подмены кадра не видно. Заголовок/лейблы кнопок — те же
 * board-i18n-ключи, что покажет board-screen (визуальная преемственность).
 */
function renderInProgressSnapshot(state: MountState, view: InProgressBoardView): void {
  teardownDynamic(state);
  // Постамент board-as-etalon — ОБЩИЙ с board-screen/idle/done (postament.css): header +
  // board + actions, БЕЗ secondary (как board-screen) → доска центрируется идентично игре.
  state.root.classList.add('postament-screen');
  state.root.innerHTML = `
    <div class="daily-rail postament-rail">
      <div class="daily-header postament-header">
        <div class="daily-titleline postament-titleline">
          <div class="daily-title postament-title screen-title">${escapeHtml(t('game.board.titleStandard'))}</div>
        </div>
      </div>

      <!-- Disabled-плейсхолдеры кнопок: держат высоту actions-ряда = board-screen (доска не
           «прыгает» при смене кадра на board-screen); рабочие кнопки навесит board-screen. -->
      <div class="daily-actions postament-actions">
        <button class="btn primary" type="button" disabled aria-busy="true">${escapeHtml(t('game.board.btnFinish'))}</button>
        <button class="btn" type="button" disabled aria-busy="true">${escapeHtml(t('game.board.btnNewGame'))}</button>
      </div>

      <div class="rail-compact" aria-hidden="true"></div>
    </div>

    <!-- Доску монтирует ЕДИНЫЙ модуль board-view (статично из снимка) — отдельной разметки
         доски нет (доска = один объект). -->
    <div class="daily-preview-wrap postament-board" id="daily-preview-wrap"></div>
  `;
  const wrap = state.root.querySelector<HTMLElement>('#daily-preview-wrap')!;
  const board = mountBoardView(wrap, {
    interactive: false,
    ariaLabel: t('game.dailyScreen.previewAria'),
  });
  // Оформление игрока (палитра×материал) на доске резюма (AC-2). teardownDynamic снимет.
  state.appearance = attachLiveAppearance(board.frame);
  board.setScore(view.score);
  board.setMoves(view.movesCount);
  board.renderNiche(view.next, true);
  board.renderStaticGrid(view.grid, view.next);
}

function renderError(state: MountState, result: DailySeedResult): void {
  teardownDynamic(state);
  const message = result.ok
    ? ''
    : result.error === 'rate_limited'
      ? t('game.dailyScreen.errRateLimited')
      : result.error === 'network'
        ? t('game.dailyScreen.errNetwork')
        : t('game.dailyScreen.errServer');

  state.root.innerHTML = `
    <div class="daily-card daily-card-error">
      <h2 class="daily-title">${escapeHtml(t('game.dailyScreen.errTitle'))}</h2>
      <p class="daily-error-msg">${escapeHtml(message)}</p>
      <div class="daily-actions">
        <button class="btn primary" id="daily-retry" type="button">${escapeHtml(t('game.dailyScreen.btnRetry'))}</button>
        <button class="btn" id="daily-casual" type="button">${escapeHtml(t('game.dailyScreen.btnFree'))}</button>
      </div>
    </div>
  `;

  state.root.querySelector<HTMLButtonElement>('#daily-retry')?.addEventListener('click', () => {
    renderLoading(state);
    void boot(state);
  });
  state.root.querySelector<HTMLButtonElement>('#daily-casual')?.addEventListener('click', () => {
    useGameStore.getState().showModeSelect();
  });

  // Подписи кнопок («Повторить» / «Свободная игра») — ЕДИНЫЙ закон шрифта кнопок
  // (group-uniform autofit, как у idle/done через action-rail kit): longest label
  // измеряется → обе кнопки одного размера = min, влезающего в «Свободная игра»; лейбл
  // НИКОГДА не вылезает за кнопку (owner-incident 2026-06-12 — error-карточка была
  // «старая», без autofit → подпись вылезала). Раскладку даёт postament-actions (ряд +
  // gap + перенос); .action-rail rail-CSS НЕ навешиваем (карточка центрированная, не
  // боковая панель). Хэндл — в state.autofit (teardownDynamic снимет на след. рендере).
  state.autofit = attachButtonGroupAutofit(state.root, '.daily-card-error .btn');
}

function renderIdle(state: MountState): void {
  if (!state.seedResult || !state.seedResult.ok) return;
  teardownDynamic(state);
  const seedRes = state.seedResult;
  const dateLabel = formatDateLabel(seedRes.dateOfDay);
  const title = t('game.dailyScreen.titleIdle');

  // T-7.2.5-fix2 (§14.2): preview = full etalon board (.board-frame, byte-equal to
  // game board). Preview lives in its own grid area (.daily-preview-wrap) outside
  // the chrome rail, so it renders at :root --board-w. Chrome (eyebrow / title /
  // sub / countdown / CTA / stats) lives in .daily-rail — stacked under the board
  // (portrait) or beside it (side-rail). Fit-switch §14.1 prevents collisions.
  // На УЗКОЙ боковой панели (.rail-is-compact) обвязка сворачивается в 2 кнопки
  // ⓘ/☰ (.rail-compact, общий action-rail kit) — РОВНО как на board; доска-превью
  // НЕ уменьшается (docs/specs/board-compact-rail.md).
  // Постамент-раскладка (styles/postament.css) — ОБЩАЯ с board (owner-directive
  // 2026-05-29 «одни и те же подходы для всех окон»): .daily-screen=.postament-screen,
  // .daily-rail=.postament-rail (stacked → display:contents → дети текут в grid
  // как header/secondary/actions; rail → absolute corner-anchored панель),
  // превью=.postament-board. По построению daily структурно = board: заголовок
  // СВЕРХУ (#6), кнопки РЯДОМ внизу как board (#3), без скролла (#7). Группы детей
  // rail'а = ровно 3 grid-области (header / secondary / actions) + хост компакта.
  state.root.classList.add('postament-screen');
  state.root.innerHTML = `
    <div class="daily-rail postament-rail">
      <div class="daily-header postament-header">
        <!-- Портрет: ЕДИНСТВЕННАЯ строка над доской = «Доска дня · дата» + кнопка «?»
             справа (owner-directive 2026-05-29). «Сегодняшняя раздача» + описание
             уходят в «?»-поповер (см. attachInfoPopover, host = .daily-titleline) →
             доска не дёргается при переходе daily↔игра. В rail (ландшафт) «?» скрыта,
             показывается полная колонка (eyebrow/title/sub). -->
        <div class="daily-titleline postament-titleline">
          <div class="daily-eyebrow postament-title screen-title">${escapeHtml(t('game.dailyScreen.eyebrow', { date: dateLabel }))}</div>
        </div>
        <div class="daily-title-row">
          <h2 class="daily-title">${escapeHtml(title)}</h2>
        </div>
        <p class="daily-sub">${escapeHtml(dailyDescriptiveCopy())}</p>
      </div>

      <div class="daily-secondary postament-secondary">
        <!-- Подпись и таймер — ОТДЕЛЬНЫЕ сущности-сёстры (owner-directive 2026-05-29
             «разорвать текст с таймером»), БЕЗ общей обёртки: подпись сверху своим
             маленьким размером, цифры отдельно кнопочного размера — не влияют друг
             на друга. -->
        <div class="daily-cd-label">${escapeHtml(t('game.dailyScreen.countdownLabel'))}</div>
        <div class="daily-cd-value" id="daily-countdown">--:--:--</div>
        <!-- «Серия»/«Лучшее место» УДАЛЕНЫ ЦЕЛИКОМ (owner п.16, тикет fe-home-screen
             AC-10): это были пустые плейсхолдеры со значением «—», не несущие информации.
             Удаление чисто презентационное — серверная meta-логика (current_daily_streak/
             daily_rank) НЕ затронута (AC-12). Карточка остаётся целостной: подпись+таймер
             выше, кнопки ниже; единая --m-щель → нет дыры/двойного gap (AC-11). НЕ
             display:none (anti-pattern #49) — узел вырезан из DOM. -->
      </div>

      <div class="daily-actions postament-actions">
        <!-- data-autofit-measure-as: ФАНТОМ-замер pending-лейбла «проверяю…». На клик
             текст кнопки свапается на этот лейбл (серверная сверка). Uniform-autofit
             группы кнопок меряет #daily-play по САМОМУ ШИРОКОМУ из {«Играть», «проверяю…»}
             → шрифт садится так, что pending влезает в ЛЮБОЙ локали (tr/vi/th/pt/es/fr,
             где «проверяю…» длиннее «Играть») БЕЗ refit/скачка при свапе (AC-8/AC-13). -->
        <button class="btn btn--cta primary daily-play-btn press-feedback" id="daily-play" type="button"
          data-autofit-measure-as="${escapeHtml(t('game.dailyScreen.btnChecking'))}">${escapeHtml(t('game.dailyScreen.btnPlay'))}</button>
        <button class="btn" id="daily-casual" type="button">${escapeHtml(t('game.dailyScreen.btnFree'))}</button>
      </div>

      <!-- Компактная форма панели (узкая --rail-w): обвязка → 2 кнопки ⓘ/☰
           (общий action-rail kit). Доска-превью НЕ уменьшается. -->
      <div class="rail-compact" aria-hidden="true"></div>
    </div>

    <!-- Превью стартовой раскладки монтирует ЕДИНЫЙ модуль board-view в .daily-
         preview-wrap при рендере — отдельной разметки доски нет (доска = один объект). -->
    <div class="daily-preview-wrap postament-board" id="daily-preview-wrap"></div>
  `;

  renderEtalonPreview(state, state.root.querySelector<HTMLElement>('#daily-preview-wrap')!, seedRes.seed);
  attachCountdown(state, (hms) => {
    const el = state.root.querySelector<HTMLElement>('#daily-countdown');
    if (el) el.textContent = hms;
  });
  attachInfoPopover(state, title);

  // ЖЁСТКИЙ server-truth гейт на действии «Играть доску дня» (cross-browser double-play
  // fix, owner-bug 2026-06-11). Локальный device-блок (`loadPlayed`) уже отработал ДО
  // idle-рендера (тот же браузер не пускает переиграть — остаётся как было). Здесь —
  // ДОПОЛНИТЕЛЬНАЯ проверка «этот АККАУНТ играл сегодня на ЛЮБОМ браузере» ПЕРЕД стартом,
  // чтобы не дать сыграть впустую партию, которую сервер потом отклонит на сабмите
  // (`duplicate_attempt`). `gateOwnDailyStats` дедупит с фоновым idle-reconcile (один
  // запрос) и кэширует ответ в сессии (повторные клики не бьют по эндпоинту).
  let gateInFlight = false; // защита от повторных кликов, пока летит проверка
  const playDaily = (): void => {
    if (gateInFlight) return;
    gateInFlight = true;
    // Кнопку — в PENDING на время серверной проверки (AC-6): лейбл «проверяю…»,
    // `disabled` + `aria-busy` — НЕ полноэкранный лоадер. БЕЗ layout-shift (AC-8): группа
    // кнопок уже измерена uniform-autofit'ом, причём #daily-play мерилась ФАНТОМ-замером по
    // САМОМУ ШИРОКОМУ из своих состояний {«Играть», «проверяю…»} (data-autofit-measure-as,
    // см. разметку выше). Поэтому pending-лейбл влезает в тот же шрифт и ту же геометрию
    // кнопки/ряда/доски-превью в ЛЮБОЙ локали (в tr/vi/th/pt/es/fr «проверяю…» ДЛИННЕЕ
    // «Играть» — фантом это и резервирует); refit НЕ зовём (он бы пересчитал размер). Свап —
    // только textContent; ширину кнопки задаёт CSS-раскладка постамента (flex), не текст.
    // Оригинальный лейбл восстанавливаем в releaseBtn (revert после ответа сервера).
    const playBtn = state.root.querySelector<HTMLButtonElement>('#daily-play');
    const originalLabel = playBtn?.textContent ?? '';
    if (playBtn) {
      playBtn.disabled = true;
      playBtn.setAttribute('aria-busy', 'true');
      playBtn.textContent = t('game.dailyScreen.btnChecking');
    }
    const releaseBtn = (): void => {
      if (playBtn) {
        playBtn.disabled = false;
        playBtn.removeAttribute('aria-busy');
        playBtn.textContent = originalLabel;
      }
      gateInFlight = false;
    };
    void gateOwnDailyStats(seedRes.dateOfDay).then((stats) => {
      // Сервер вернул запись → этот аккаунт УЖЕ играл (другой браузер). НЕ стартуем:
      // backfill локально + флип в done + поясняющий тост. Guard isConnected — экран
      // мог смениться, пока летел запрос.
      if (stats) {
        if (!state.root.isConnected) {
          gateInFlight = false;
          return;
        }
        backfillPlayedFromServerStats(state, seedRes.dateOfDay, stats);
        renderDone(state);
        showToast(t('game.dailyScreen.toastAlreadyPlayed'));
        // renderDone пересоздаёт DOM (teardown) — releaseBtn не нужен (кнопки уже нет).
        gateInFlight = false;
        return;
      }
      // null = «не играл» / гость / сетевой сбой → СТАРТУЕМ. На сетевой ошибке РАЗРЕШАЕМ
      // старт (сервер backstop-ит на сабмите) — не запираем офлайн-игрока (fail-open).
      releaseBtn();
      useGameStore.getState().startDailyGame({
        seed: seedRes.seed,
        dateOfDay: seedRes.dateOfDay,
      });
    });
  };
  // «Играть» (бывш. casual) → экран-селектор режимов (extra-modes §5.0 / AC-1.3),
  // а НЕ мгновенный старт predict-casual. «Играть доску дня» (#daily-play) не
  // меняется (predict-only daily, §3.2).
  const playCasual = (): void => {
    useGameStore.getState().showModeSelect();
  };

  const playBtnEl = state.root.querySelector<HTMLButtonElement>('#daily-play');
  playBtnEl?.addEventListener('click', playDaily);
  state.root.querySelector<HTMLButtonElement>('#daily-casual')?.addEventListener('click', playCasual);

  // Attract-беат «Играть» (триггерный, owner-final 2026-06-28; lib/attract-beat —
  // общий модуль, тот же на board): мягкий намёк через ~600мс после захода (заметить
  // кнопку) + сильный зов на промах-тап по доске-превью (превью не активно, тап = промах).
  // Per-render handle'ы → teardownDynamic снимет (таймер намёка + слушатель тапа).
  const previewWrapEl = state.root.querySelector<HTMLElement>('#daily-preview-wrap');
  if (playBtnEl) {
    state.softBeat = scheduleSoftBeat(playBtnEl);
    if (previewWrapEl) state.nudge = attachBoardNudge(previewWrapEl, playBtnEl);
  }

  // ОБЩИЙ action-rail kit (lib/action-rail.ts) — autofit кнопок (line+uniform+
  // дельта) + компакт ⓘ/☰ на узкой боковой панели, РОВНО как на board. Текстовые
  // элементы обвязки — отдельный autofit ниже. Применяется ТОЛЬКО к idle: это
  // единственный daily-state с board-preview postament + side-rail (done/error —
  // центрированные chrome-карты, у них нет превью-доски для защиты и нет узкой
  // боковой панели → компакт там не нужен и не должен ложно срабатывать).
  attachDailyActionRail(state, { dateLabel, title, playDaily, playCasual });

  // Autofit текстовых элементов панели ИЗМЕРЕНИЕМ (capToDesign: дизайн-размер =
  // потолок, лишь уменьшаются при нехватке ширины узкой панели). countdown-value —
  // крупный акцент (своего дизайн-размера --fs-kpi-v), тоже capToDesign. min-width:0
  // на flex-контейнеры текста — в CSS (daily.css), чтобы autofit мерил реальную
  // ширину панели, а не раздутую по контенту.
  state.autofit = attachAutofit(state.root, [
    // Портрет (stacked): eyebrow-заголовок в ОДНУ строку — line-mode ужимает по
    // ширине (nowrap из постамента не обрезает), потолок = дизайн → высота шапки
    // детерминирована = board → доска не дёргается daily↔игра (owner 2026-05-29).
    { selector: '.daily-eyebrow', mode: 'line', fillRatio: SCREEN_TITLE_FILL, capToDesign: true, onlyLayout: 'stacked' },
    // Ландшафт (rail): eyebrow крупный (cell·0.8 из .screen-title), перенос по словам.
    { selector: '.daily-eyebrow', fillRatio: SCREEN_TITLE_FILL, capToDesign: true, onlyLayout: 'rail' },
    { selector: '.daily-title', capToDesign: true },
    { selector: '.daily-sub', capToDesign: true },
    { selector: '.daily-cd-label', capToDesign: true },
    // Таймер-цифры — ТОТ ЖЕ measurement-autofit, что и кнопки (line + общая дельта
    // ACTION_RAIL_FILL), БЕЗ capToDesign-потолка (owner-directive 2026-05-29: «шрифт
    // таймера такой же как на кнопках; capToDesign = зажим = хардкод»). В stacked
    // у .daily-cd-value высота кнопки (CSS) → line-mode даёт шрифт = кнопочный.
    { selector: '.daily-cd-value', mode: 'line', fillRatio: ACTION_RAIL_FILL },
    // .daily-stat-k/.daily-stat-v autofit-проводка УДАЛЕНА вместе с плейсхолдером
    // «Серия/Лучшее место» (owner п.16, AC-10) — этих узлов больше нет в DOM.
  ]);
}

/**
 * Подключить общий action-rail kit к idle-панели daily. Кнопки действий
 * (Играть / casual) — autofit + компакт ⓘ/☰ на узкой панели. ⓘ-попап = весь
 * описательный текст (eyebrow / заголовок / подзаголовок / countdown / статистика);
 * ☰-попап = действия. Согласовано с `?`-InfoPopover (тот для descriptive copy на
 * touch; компакт-ⓘ — отдельный механизм узкой боковой панели).
 */
function attachDailyActionRail(
  state: MountState,
  ctx: { dateLabel: string; title: string; playDaily: () => void; playCasual: () => void },
): void {
  const rail = state.root.querySelector<HTMLElement>('.daily-rail');
  const actionsContainer = state.root.querySelector<HTMLElement>('.daily-actions');
  const compactHost = state.root.querySelector<HTMLElement>('.rail-compact');
  const playBtn = state.root.querySelector<HTMLButtonElement>('#daily-play');
  const casualBtn = state.root.querySelector<HTMLButtonElement>('#daily-casual');
  if (!rail || !actionsContainer || !compactHost || !playBtn || !casualBtn) return;

  state.actionRail = attachActionRail(state.root, {
    rail,
    actionsContainer,
    compactHost,
    actionButtons: [playBtn, casualBtn],
    infoTitle: ctx.title,
    buildInfoBody: () => buildDailyInfoBody(state, ctx.dateLabel, ctx.title),
    buildActionsBody: (close) => buildDailyActionsBody(ctx.playDaily, ctx.playCasual, close),
  });
}

/** Тело ⓘ-попапа daily: eyebrow + заголовок + подзаголовок + countdown + статистика. */
function buildDailyInfoBody(state: MountState, dateLabel: string, title: string): HTMLElement {
  const wrap = document.createElement('div');

  const eyebrow = document.createElement('div');
  eyebrow.className = 'rail-popover-text-sub';
  eyebrow.textContent = t('game.dailyScreen.eyebrow', { date: dateLabel });

  const titleEl = document.createElement('div');
  titleEl.className = 'rail-popover-text-title';
  titleEl.textContent = title;

  const sub = document.createElement('div');
  sub.className = 'rail-popover-text-sub';
  sub.textContent = dailyDescriptiveCopy();

  const stats = document.createElement('div');
  stats.className = 'rail-popover-text-stats';
  // Live-снимок countdown'а (попап перестраивается при каждом открытии).
  const cdValue = state.root.querySelector<HTMLElement>('#daily-countdown')?.textContent ?? '--:--:--';
  const mkRow = (label: string, value: string): HTMLElement => {
    const span = document.createElement('span');
    span.append(document.createTextNode(`${label}: `));
    const b = document.createElement('b');
    b.textContent = value;
    span.append(b);
    return span;
  };
  // ⓘ-попап daily: «Серия»/«Лучшее место» УДАЛЕНЫ для консистентности с карточкой
  // (owner п.16, тикет fe-home-screen, решение оркестратора): это были те же пустые
  // плейсхолдеры со значением «—», что и на карточке (.daily-stats-placeholder). Реального
  // значения они не несли → убраны и здесь. Остаётся живой countdown-ряд.
  stats.append(mkRow(t('game.dailyScreen.countdownLabel'), cdValue));

  wrap.append(eyebrow, titleEl, sub, stats);
  return wrap;
}

/** Тело ☰-попапа daily: кнопки действий полными лейблами (те же store-действия). */
function buildDailyActionsBody(
  playDaily: () => void,
  playCasual: () => void,
  close: () => void,
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'rail-popover-actions';

  const play = document.createElement('button');
  play.type = 'button';
  play.className = 'btn primary';
  play.textContent = t('game.dailyScreen.btnPlay');
  play.addEventListener('click', () => {
    playDaily();
    close();
  });

  const casual = document.createElement('button');
  casual.type = 'button';
  casual.className = 'btn';
  casual.textContent = t('game.dailyScreen.btnFree');
  casual.addEventListener('click', () => {
    playCasual();
    close();
  });

  wrap.append(play, casual);
  return wrap;
}

/**
 * Done-state (core-daily-board.md §2.3-DONE, owner-directive 2026-06-09): ФИНАЛЬНАЯ
 * доска игрока в постамент-раскладке (как игровой экран / idle). Обвязка = 4 ЭЛЕМЕНТА
 * ОДНОГО РАЗМЕРА (owner 2026-06-09 «все 4 одного размера, для симметрии»): две плашки-
 * показателя (Ранг, Таймер) + две кнопки (Реплей, Поделиться) в сетке 2×2 (stacked) /
 * колонке (rail). Все — на базе `.btn` с ЕДИНЫМ uniform-autofit → одинаковые размер и
 * шрифт. Плашки — примитив `stat-plate` (масштаб от токенов, без хардкода). Доска из
 * кэш-снимка мгновенно; Ранг и (при промахе кэша) восстановление доски — async.
 */
function renderDone(state: MountState): void {
  if (!state.seedResult || !state.seedResult.ok || !state.played) return;
  teardownDynamic(state);
  const seedRes = state.seedResult;
  const played = state.played;
  const dateLabel = formatDateLabel(seedRes.dateOfDay);
  const title = t('game.dailyScreen.titleDone');

  // ctx.shortId — единый источник ключа реплея для кнопок Реплей/Поделиться:
  // изначально из кэша, hydrateDone дописывает его из leaderboard.me (чужое
  // устройство), чтобы кнопки работали даже когда снимка/стора нет.
  const ctx: { shortId: string | null } = { shortId: played.shortId ?? null };

  // Постамент-раскладка — ОБЩАЯ с board / idle (owner «одни подходы для всех окон»):
  // доска в .postament-board, обвязка в .postament-rail. Слот actions несёт сетку
  // .daily-grid-4 из 4 равных элементов; секондари НЕ рендерим (его фикс-высота не
  // нужна — экономим вертикаль).
  state.root.classList.add('postament-screen');
  state.root.innerHTML = `
    <div class="daily-rail postament-rail">
      <div class="daily-header postament-header">
        <div class="daily-titleline postament-titleline">
          <div class="daily-eyebrow postament-title screen-title">${escapeHtml(t('game.dailyScreen.eyebrow', { date: dateLabel }))}</div>
        </div>
        <div class="daily-title-row">
          <h2 class="daily-title">${escapeHtml(title)}</h2>
        </div>
      </div>

      <!-- 4 элемента одного размера: [Ранг][Таймер] / [Реплей][Поделиться]. Плашки
           (примитив stat-plate) монтируются первыми (prepend) ниже. -->
      <div class="daily-actions daily-grid-4 postament-actions">
        <button class="btn primary" id="daily-replay" type="button">${escapeHtml(t('game.replay'))}</button>
        <button class="btn" id="daily-share" type="button">${escapeHtml(t('game.dailyScreen.btnShare'))}</button>
      </div>

      <!-- Компактная форма панели (узкий боковой rail, напр. предквадратный 320×384):
           обвязка сворачивается в 2 кнопки ⓘ/☰ (общий action-rail kit, как игровой
           экран). На широком rail (телефон owner'а в ландшафте) НЕ срабатывает. -->
      <div class="rail-compact" aria-hidden="true"></div>
    </div>

    <!-- Финальную доску игрока монтирует ЕДИНЫЙ модуль board-view в .daily-preview-
         wrap (статично) — отдельной разметки доски нет (доска = один объект). -->
    <div class="daily-preview-wrap postament-board" id="daily-preview-wrap"></div>
  `;

  // Финальная доска — единый модуль board-view (статичный режим). Из локального
  // снимка рисуем СРАЗУ и финально (instant-ui-hydration §1). Снимка нет (кросс-девайс
  // / чужой браузер) → скелет той же геометрии (класс `--awaiting` на wrap, zero
  // layout-shift), пока пересимуляция в `hydrateDone` не заполнит доску (§5 — стабильный
  // приглушённый плейсхолдер, не пустая доска со скачком; счёт уже известен на плашке).
  const previewWrap = state.root.querySelector<HTMLElement>('#daily-preview-wrap')!;
  const board = mountBoardView(previewWrap, {
    interactive: false,
    ariaLabel: t('game.dailyScreen.doneBoardAria'),
  });
  // Оформление игрока на финальной доске дня (AC-2). teardownDynamic снимет handle.
  state.appearance = attachLiveAppearance(board.frame);
  if (played.finalBoard) {
    paintFinalBoard(board, played.finalBoard, played.scoreVerified);
  } else {
    board.setScore(played.scoreVerified);
    board.setMoves(0);
    previewWrap.classList.add('daily-board--awaiting');
  }

  // Две плашки-показателя (ПРИМИТИВ stat-plate, база .btn → размер/шрифт от токенов,
  // масштаб на 4К). Ранг — с подписью; Таймер — только значение (HH:MM:SS). prepend
  // в порядке Ранг → Таймер, чтобы DOM-порядок был [Ранг][Таймер][Реплей][Поделиться]
  // → в сетке 2×2 верхний ряд = плашки, нижний = кнопки.
  const actions = state.root.querySelector<HTMLElement>('.daily-grid-4')!;
  // LAST-KNOWN ранг из снимка → плашка показывает его СРАЗУ (instant-ui-hydration §3),
  // не «—» до async `fetchOwnDailyStats` (owner-playtest fix 2026-06-11 «ранг мелькает»).
  // Ранг живой за день → hydrateDone тихо ресинкает к server-truth (idempotent: совпало =
  // no-op, плашка не дёргается). Снимка ранга нет (кросс-девайс) → «—», как раньше.
  const rankPlate = createStatPlate({
    label: t('game.dailyScreen.rankLabel'),
    ...(played.lastRank !== undefined ? { initialValue: String(played.lastRank) } : {}),
  });
  // Таймер — ЕДИНЫЙ примитив `components/countdown-plate` (ТОТ ЖЕ импорт, что на игровом
  // экране доски дня): value-only, только HH:MM:SS без слов (подпись в aria). На роллровере
  // (00:00:00) done-state ребутит доску к новому дню (idle становится играбельным). Тик
  // останавливает teardownDynamic через state.countdown.
  const timer = createCountdownPlate({
    onRollover: () => {
      clearDailySeedCache();
      renderLoading(state);
      void boot(state);
    },
  });
  state.countdown = timer;
  // Цвет текста (owner 2026-06-09): Ранг = как цифры счёта в лидерборде (--accent);
  // Таймер = как заголовок «Счёт» (--ink-soft). Классы-модификаторы → стили в daily.css.
  rankPlate.element.classList.add('daily-plate-rank');
  timer.element.classList.add('daily-plate-timer');
  actions.prepend(timer.element);
  actions.prepend(rankPlate.element);

  // «Реплей» — по short_id (кросс-сессионно, как из лидерборда); запасной путь —
  // реплей из стора той же сессии. «Поделиться» — копирует ссылку /r/<short_id>.
  state.root.querySelector<HTMLButtonElement>('#daily-replay')?.addEventListener('click', () => {
    openOwnReplay(ctx.shortId);
  });
  state.root.querySelector<HTMLButtonElement>('#daily-share')?.addEventListener('click', () => {
    shareOwnDailyReplay(ctx.shortId);
  });

  // Таймер done-state self-driven примитивом countdown-plate выше (с onRollover-ребутом);
  // отдельный attachCountdown тут больше не нужен (idle по-прежнему использует его).

  // Общий action-rail kit (как игровой экран): ЕДИНЫЙ uniform-autofit всех 4
  // элементов сетки (.action-rail .btn → плашки + кнопки одинакового размера) +
  // компакт ⓘ/☰ на узком боковом rail (предквадратный случай прячет обвязку в
  // «сэндвич», доска не уменьшается; на широком rail НЕ срабатывает).
  attachDoneActionRail(state, { title, ctx });

  // Autofit ТЕКСТОВ шапки (eyebrow line/rail + title) — кнопки/плашки делает
  // attachActionRail выше (uniform-группа .action-rail .btn).
  state.autofit = attachAutofit(state.root, [
    { selector: '.daily-eyebrow', mode: 'line', fillRatio: SCREEN_TITLE_FILL, capToDesign: true, onlyLayout: 'stacked' },
    { selector: '.daily-eyebrow', fillRatio: SCREEN_TITLE_FILL, capToDesign: true, onlyLayout: 'rail' },
    { selector: '.daily-title', capToDesign: true },
  ]);

  // Асинхронно: Ранг из лидерборда (свой `me`-ряд); при промахе кэша — финальную
  // доску пересимуляцией. Безопасно при уходе с экрана (guard isConnected).
  void hydrateDone(state, board, rankPlate, ctx);
}

/** Отрисовать финальную доску из снимка: счёт/ходы на плашке + статичный грид+ниша. */
function paintFinalBoard(board: BoardViewHandle, snap: FinalBoardSnapshot, score: number): void {
  board.setScore(score);
  board.setMoves(snap.movesCount);
  board.renderNiche(snap.next, true);
  board.renderStaticGrid(snap.grid, snap.next);
}

/**
 * Открыть свой реплей дня: по `short_id` (кросс-сессионно, тот же путь, что из
 * лидерборда — `showReplayById`); если short_id нет, но стор ещё держит ходы той
 * же сессии — локальный реплей. Иначе ничего (нет данных для реплея).
 */
function openOwnReplay(shortId: string | null): void {
  const store = useGameStore.getState();
  if (shortId) {
    store.showReplayById(shortId);
    return;
  }
  if (store.moves.length > 0) store.showReplay();
}

/**
 * «Поделиться» — копирует ссылку на свой реплей `/r/<short_id>` (как result-screen,
 * переиспользует lib/share-link). До прихода short_id (нет данных) — поясняющий тост.
 */
function shareOwnDailyReplay(shortId: string | null): void {
  if (!shortId) {
    showToast(t('game.dailyScreen.toastNoLink'));
    return;
  }
  void copyShareLink(`/r/${shortId}`).then((r) => {
    showToast(r.ok ? t('game.dailyScreen.toastCopied') : t('game.dailyScreen.toastCopyFail'));
  });
}

/**
 * Дотянуть done-состояние: Ранг из дневного лидерборда (свежий — меняется за день),
 * и при отсутствии кэш-снимка — финальную доску пересимуляцией по своему `short_id`.
 * Все правки под guard'ом `board.frame.isConnected` (экран мог смениться).
 */
async function hydrateDone(
  state: MountState,
  board: BoardViewHandle,
  rankPlate: { setValue(v: string): void; element: HTMLElement },
  ctx: { shortId: string | null },
): Promise<void> {
  const seedRes = state.seedResult;
  const played = state.played;
  if (!seedRes || !seedRes.ok || !played) return;

  const stats = await fetchOwnDailyStats(seedRes.dateOfDay);
  if (!board.frame.isConnected) return;
  if (stats) {
    // ИДЕМПОТЕНТНЫЙ reconcile (instant-ui-hydration §3): пишем в плашку Ранг + перефитим
    // ТОЛЬКО если server-truth реально отличается от LAST-KNOWN (снимок → instant-paint
    // выше). Сравниваем ЧИСЛА (не подстроку — «4» vs «42» ложно совпали бы), чтобы guard
    // не залипал при смене ранга 42→4 за день. Совпало → no-op, плашка не дёргается.
    if (played.lastRank !== stats.rank) {
      // Подпись «Ранг» даёт примитив → значение = число (текст «Ранг: 42»).
      rankPlate.setValue(String(stats.rank));
      state.autofit?.refit();
      // Кэшируем свежий ранг в played-запись (снимок + IDB-канон одним write-path'ом) →
      // СЛЕДУЮЩИЙ mount красит плашку этим рангом мгновенно, без «мелькания» (§3). Ранг —
      // UX-проекция показа: server-truth перетирает её при каждом hydrateDone (boundary
      // daily-cache; анти-чит цел — ранг не вход для проверок). Перезапись equal-снимка
      // безвредна, но мы здесь УЖЕ в ветке «ранг изменился».
      void persistRank(state, stats.rank);
    }
    if (!ctx.shortId) ctx.shortId = stats.shortId;
  }

  // Кэш-снимка нет (другое устройство / очищенный браузер) → пересимуляция по своему
  // short_id (из кэша или из leaderboard.me). Результат кэшируем → следующий заход
  // мгновенный.
  if (!played.finalBoard) {
    const shortId = ctx.shortId;
    if (shortId) {
      const resim = await fetchFinalBoardByShortId(shortId);
      if (resim && board.frame.isConnected) {
        const score = played.scoreVerified || resim.score;
        paintFinalBoard(board, resim.board, score);
        // Скелет ожидания снят — доска заполнена пересимуляцией (§5: плавный переход
        // из приглушённого плейсхолдера в реальную доску, без layout-shift).
        board.frame.closest('.daily-preview-wrap')?.classList.remove('daily-board--awaiting');
        const filled: PlayedRecord = {
          v: 1,
          dateOfDay: seedRes.dateOfDay,
          scoreVerified: score,
          replayId: played.replayId,
          linesVerified: resim.lines,
          shortId,
          finalBoard: resim.board,
          // Сохраняем last-known ранг (если уже знаем) вместе с восстановленной доской.
          ...(stats?.rank !== undefined
            ? { lastRank: stats.rank }
            : played.lastRank !== undefined
              ? { lastRank: played.lastRank }
              : {}),
          timestamp: Date.now(),
        };
        state.played = filled;
        void savePlayed(filled);
      }
    }
  }
}

/**
 * Перезаписать played-запись текущего дня с обновлённым `lastRank` (снимок + IDB-канон
 * одним write-path'ом `savePlayed`). Сохраняет все прочие поля записи неизменными.
 * Вызывается из `hydrateDone`, когда ранг реально изменился vs снимка — чтобы СЛЕДУЮЩИЙ
 * mount красил плашку Ранг мгновенно (instant-ui-hydration §3). `state.played` тоже
 * обновляется, чтобы reconcile в этой же сессии не зацикливался.
 */
async function persistRank(state: MountState, rank: number): Promise<void> {
  const played = state.played;
  if (!played) return;
  const next: PlayedRecord = { ...played, lastRank: rank };
  state.played = next;
  await savePlayed(next);
}

/**
 * Подключить общий action-rail kit к done-сетке: uniform-autofit всех 4 элементов
 * (.action-rail .btn — плашки + кнопки одного размера) + компакт ⓘ/☰ на узком rail
 * (как игровой экран). ⓘ-попап = показатели (Ранг + Таймер); ☰-попап = действия
 * (Реплей / Поделиться). actionButtons (замер ширины для триггера компакта) — только
 * реальные кнопки.
 */
function attachDoneActionRail(
  state: MountState,
  ctx: { title: string; ctx: { shortId: string | null } },
): void {
  const rail = state.root.querySelector<HTMLElement>('.daily-rail');
  const actionsContainer = state.root.querySelector<HTMLElement>('.daily-grid-4');
  const compactHost = state.root.querySelector<HTMLElement>('.rail-compact');
  const replayBtn = state.root.querySelector<HTMLButtonElement>('#daily-replay');
  const shareBtn = state.root.querySelector<HTMLButtonElement>('#daily-share');
  if (!rail || !actionsContainer || !compactHost || !replayBtn || !shareBtn) return;

  state.actionRail = attachActionRail(state.root, {
    rail,
    actionsContainer,
    compactHost,
    actionButtons: [replayBtn, shareBtn],
    infoTitle: ctx.title,
    buildInfoBody: () => buildDoneInfoBody(state),
    buildActionsBody: (close) => buildDoneActionsBody(ctx.ctx, close),
  });
}

/** Тело ⓘ-попапа done (узкий rail): показатели — Ранг + Таймер (живые значения плашек). */
function buildDoneInfoBody(state: MountState): HTMLElement {
  const wrap = document.createElement('div');
  const stats = document.createElement('div');
  stats.className = 'rail-popover-text-stats';
  const rankTxt =
    state.root.querySelector<HTMLElement>('.daily-plate-rank')?.textContent ??
    `${t('game.dailyScreen.rankLabel')}: —`;
  const timerTxt = state.root.querySelector<HTMLElement>('.daily-plate-timer')?.textContent ?? '--:--:--';
  const mk = (text: string): HTMLElement => {
    const span = document.createElement('span');
    span.textContent = text;
    return span;
  };
  stats.append(mk(rankTxt), mk(`${t('game.dailyScreen.countdownLabel')}: ${timerTxt}`));
  wrap.append(stats);
  return wrap;
}

/** Тело ☰-попапа done (узкий rail): действия — Реплей / Поделиться. */
function buildDoneActionsBody(ctx: { shortId: string | null }, close: () => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'rail-popover-actions';

  const replay = document.createElement('button');
  replay.type = 'button';
  replay.className = 'btn primary';
  replay.textContent = t('game.replay');
  replay.addEventListener('click', () => {
    openOwnReplay(ctx.shortId);
    close();
  });

  const share = document.createElement('button');
  share.type = 'button';
  share.className = 'btn';
  share.textContent = t('game.dailyScreen.btnShare');
  share.addEventListener('click', () => {
    shareOwnDailyReplay(ctx.shortId);
    close();
  });

  wrap.append(replay, share);
  return wrap;
}

// =============================================================================
// Etalon preview rendering (startState → full .board-frame, byte-equal to game)
// =============================================================================

/**
 * Render the daily starting position as a FULL etalon board (§14.2,
 * design-tokens-unified.md): same DOM structure / classes as `board-screen.ts`
 * and `replay-screen.ts` (`.board` 9×9 of `.cell` + `.ball`; `.fn-niche` of
 * `.fn-slot` + `.ball`). Non-interactive — no click handlers, no store wiring;
 * it is a static snapshot of `startState(seed)`. The CSS sizes it from the
 * `:root` board geometry tokens, so the preview is byte-equal to the in-game
 * board at the same viewport (board-as-etalon foundational invariant).
 *
 * @param boardHost The `.board` grid container.
 * @param nicheHost The `.fn-niche` slot container.
 * @param seed Today's daily seed.
 */
function renderEtalonPreview(state: MountState, wrapHost: HTMLElement, seed: number): void {
  // startState на daily seed — тот же движок, что live (anti-cheat invariant).
  const st = startState(seed);
  // Превью через ЕДИНЫЙ модуль доски (board-view), статичный режим. Плашка
  // «Очки 0 / Ходы 0 / 3 шара»; daily = predict → призраки на клетках + ниша.
  // board-view без observers → достаточно вмонтировать; при ре-рендере renderIdle
  // wrap пересоздаётся (innerHTML), старый frame исчезает.
  const board = mountBoardView(wrapHost, {
    interactive: false,
    ariaLabel: t('game.dailyScreen.previewAria'),
  });
  // Оформление игрока на превью стартовой раскладки (AC-2). teardownDynamic снимет
  // предыдущий handle перед след. рендером (frame пересоздаётся через innerHTML).
  state.appearance = attachLiveAppearance(board.frame);
  board.setScore(0);
  board.setMoves(0);
  board.renderNiche(st.next, true);
  board.renderStaticGrid(st.grid, st.next);
}

// =============================================================================
// Countdown attachment helper
// =============================================================================

/**
 * Запустить обратный отсчёт до следующей доски. `setText` — куда писать HH:MM:SS
 * (idle пишет в `#daily-countdown`; done — в плашку-таймер через её setValue),
 * чтобы ОДИН модуль обслуживал оба state'а (не дублировать rollover-логику).
 */
function attachCountdown(state: MountState, setText: (hms: string) => void): void {
  // Stop prior countdown если этот renderer is а re-render.
  if (state.countdown) {
    state.countdown.stop();
    state.countdown = null;
  }
  let rolledOver = false;
  state.countdown = startCountdown((hms, msRemaining) => {
    setText(hms);
    // Rollover reached in an open tab (core-daily-board.md §5.4): the cached seed
    // now belongs to the previous day. Invalidate the in-memory seed cache once,
    // re-fetch the new day's seed, and re-render. The daily idle screen is passive
    // (no in-progress attempt here — auto-finish of a live attempt is the board
    // screen's job, T-12 trigger A), so we simply refresh to the new board.
    if (msRemaining <= 0 && !rolledOver) {
      rolledOver = true;
      clearDailySeedCache();
      renderLoading(state);
      void boot(state);
    }
  });
}

// =============================================================================
// Helpers
// =============================================================================

// `formatDateLabel` moved to `lib/format.ts` (shared with the daily leaderboard
// header — single source, no drift; core-daily-board.md §2.4). Imported above.

function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&'
      ? '&amp;'
      : ch === '<'
        ? '&lt;'
        : ch === '>'
          ? '&gt;'
          : '&quot;',
  );
}
